import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import Permission, {
  createDummyPermissions,
} from '../../interfaces/permission';
import { Apollo, QueryRef } from 'apollo-angular';
import { ConsentRequest } from '../../interfaces/consent-request';
import { ConsentStatus } from '../../interfaces/consent-status';
import { ConsentRequestType } from '../../interfaces/consent-request-type';
import { GET_REQUEST_WITH_APPROVALS } from './individual-approvals-dialog/graphql/individual-approvals';
import { Paginator, PaginatorInfo } from '../../types/paginator';
import { ConsentRequestFilterInput } from '../consent-requests/graphql/consent-requests';
import ID from '../../types/id';
import ConsentRequiredStatus from '../../interfaces/consent-required-status';
import ConsentApprovalStatus from '../../interfaces/consent-approval-status';
import {
  GET_REQUESTS_WITH_APPROVALS,
  REFRESH_CONSENT_REQUESTS_WITH_APPROVALS,
  RefreshRequestsWithApprovalsResponse,
  RefreshRequestsWithApprovalsVariables,
} from './graphql/consent-approvals';

@Injectable({
  providedIn: 'root',
})
export class ConsentApprovalsService {
  private _consentRequests: Paginator<ConsentRequest, PaginatorInfo> = {
    data: [],
    paginatorInfo: {},
  };
  private consentRequests = new BehaviorSubject(this._consentRequests);
  public consentRequests$ = this.consentRequests.asObservable();

  private _consentStatuses: ConsentStatus[] = [];
  private consentStatuses = new BehaviorSubject(this._consentStatuses);
  public consentStatuses$ = this.consentStatuses.asObservable();

  private _consentRequestTypes: ConsentRequestType[] = [];
  private consentRequestTypes = new BehaviorSubject(this._consentRequestTypes);
  public consentRequestTypes$ = this.consentRequestTypes.asObservable();

  private _permission: Permission = createDummyPermissions();
  private permission = new BehaviorSubject(this._permission);
  public permission$ = this.permission.asObservable();

  private _loading = false;
  private loading = new BehaviorSubject(this._loading);
  public loading$ = this.loading.asObservable();

  private _approvalsLoading = false;
  private approvalsLoading = new BehaviorSubject(this._approvalsLoading);
  public approvalsLoading$ = this.approvalsLoading.asObservable();

  private _consentRequiredStatuses: ConsentRequiredStatus[] = [];
  private consentRequiredStatuses = new BehaviorSubject(
    this._consentRequiredStatuses
  );
  public consentRequiredStatuses$ = this.consentRequiredStatuses.asObservable();

  private _consentApprovalStatuses: ConsentApprovalStatus[] = [];
  private consentApprovalStatuses = new BehaviorSubject(
    this._consentApprovalStatuses
  );
  public consentApprovalStatuses$ = this.consentApprovalStatuses.asObservable();
  private pageQuery: QueryRef<
    RefreshRequestsWithApprovalsResponse,
    RefreshRequestsWithApprovalsVariables
  >;
  private sub: Subscription;
  constructor(public apollo: Apollo) {}

  private _setConsentStatuses(consentStatuses: ConsentStatus[]) {
    this._consentStatuses = consentStatuses;
    this.consentStatuses.next(this._consentStatuses);
  }
  private _setConsentRequestTypes(consentRequestTypes: ConsentRequestType[]) {
    this._consentRequestTypes = consentRequestTypes;
    this.consentRequestTypes.next(this._consentRequestTypes);
  }
  private _setConsentRequests(
    consentRequests: Paginator<ConsentRequest, PaginatorInfo>
  ) {
    this._consentRequests = consentRequests;
    this.consentRequests.next(this._consentRequests);
  }
  private _setPermission(permission: Permission) {
    this._permission = permission;
    this.permission.next(this._permission);
  }
  private _setApprovalsLoading(loading: boolean) {
    this._approvalsLoading = loading;
    this.approvalsLoading.next(this._approvalsLoading);
  }
  private _setLoading(loading: boolean) {
    this._loading = loading;
    this.loading.next(this._loading);
    this._setApprovalsLoading(loading);
  }

  private _setConsentRequiredStatuses(statuses: ConsentRequiredStatus[]) {
    this._consentRequiredStatuses = statuses;
    this.consentRequiredStatuses.next(this._consentRequiredStatuses);
  }
  private _setConsentApprovalStatuses(statuses: ConsentApprovalStatus[]) {
    this._consentApprovalStatuses = statuses;
    this.consentApprovalStatuses.next(this._consentApprovalStatuses);
  }
  public getConsentRequestsWithApprovals() {
    this._setLoading(true);
    this.apollo
      .query({
        query: GET_REQUESTS_WITH_APPROVALS,
        variables: { page: 1, excludeStatuses: ['Complete', 'Denied', 'GP Withdrew'] },
        fetchPolicy: 'network-only',
      })
      .subscribe(({ data }) => {
        this._setLoading(false);
        if (!data) return;
        this._setConsentStatuses(data.getConsentStatuses);
        this._setConsentRequestTypes(data.getConsentRequestTypes);
        this._setConsentRequiredStatuses(data.getConsentRequiredStatuses);
        this._setConsentApprovalStatuses(data.getConsentApprovalStatuses);
        this._setConsentRequests(
          data.getConsentRequests as Paginator<ConsentRequest, PaginatorInfo>
        );
        if (data.me.permissionsForResource) {
          this._setPermission(data.me.permissionsForResource);
        }
      });
  }

  public getRequiredStatus(
    consent_required_status_id: ID | null
  ): ConsentRequiredStatus | null {
    return (
      this._consentRequiredStatuses.find(
        (status) => status.id === consent_required_status_id
      ) ?? null
    );
  }
  public getApprovalStatus(
    consent_approval_status_id: ID | null
  ): ConsentApprovalStatus | null {
    return (
      this._consentApprovalStatuses.find(
        (status) => status.id === consent_approval_status_id
      ) ?? null
    );
  }
  public getPage(
    page: number,
    filter?: ConsentRequestFilterInput,
    showComplete?: boolean
  ) {
    this._setApprovalsLoading(true);
    let excludeStatuses: string[] | null = ['Complete', 'Denied', 'GP Withdrew'];
    if (showComplete === true) excludeStatuses = null;
    if (filter?.consent_status_id) {
      excludeStatuses = null;
    }
    this.pageQuery = this.apollo.watchQuery<
      RefreshRequestsWithApprovalsResponse,
      RefreshRequestsWithApprovalsVariables
    >({
      query: REFRESH_CONSENT_REQUESTS_WITH_APPROVALS,
      variables: {
        page,
        excludeStatuses,
        filter,
      },
      fetchPolicy: 'network-only',
    });
    this.sub = this.pageQuery.valueChanges.subscribe(({ data }) => {
      this._setApprovalsLoading(false);
      if (data) {
        this._setConsentRequests(
          data.getConsentRequests as Paginator<ConsentRequest, PaginatorInfo>
        );
      }
    });
  }
  public tearDown() {
    this.sub?.unsubscribe();
  }
  public getRequestType(id: ID | null): ConsentRequestType | null {
    return (
      this._consentRequestTypes.find((requestType) => requestType.id === id) ??
      null
    );
  }
  public getStatus(id: ID | null): ConsentStatus | null {
    return this._consentStatuses.find((status) => status.id === id) ?? null;
  }
}
